import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Trophy, Crown, Clock, Flame, CalendarDays, Users } from 'lucide-react'
import { db } from '../firebase/config'
import { collection, getDocs } from 'firebase/firestore'
import './RadioPageTV.css'
import './ClassementPageTV.css'

/* =====================================================================
   CLASSEMENT TV — même style que l'accueil TV et les Paramètres TV.
   Podium à gauche, places 4 à 10 à droite, tout sur un seul écran.
   À la télécommande : ← → pour changer de critère, OK pour choisir.
   ===================================================================== */

const FILTERS = [
  { id: 'time',     label: "Temps d'écoute", icon: Clock,        key: 'totalListenTime' },
  { id: 'streak',   label: 'Jours de suite', icon: Flame,        key: 'streak' },
  { id: 'sessions', label: 'Sessions',       icon: CalendarDays, key: 'sessionsCount' }
]

const watchTimeOf = (d) => Math.max(
  Number(d.totalListenTime || 0),
  Number(d.totalListeningTime || 0),
  Number(d.stats?.totalSecondsWatched || 0),
  Number(d.totalSecondsWatched || 0)
)

const nameOf = (d) => {
  if (d.displayName?.trim() && d.displayName !== 'Utilisateur') return d.displayName.trim()
  if (d.firstName || d.lastName) return `${d.firstName?.trim() || ''} ${d.lastName?.trim() || ''}`.trim()
  if (d.username?.trim()) return d.username.trim()
  if (d.email) {
    const n = d.email.split('@')[0]
    return n.charAt(0).toUpperCase() + n.slice(1)
  }
  return null
}

const initialsOf = (name) => {
  const parts = name.split(/\s+/).filter(Boolean)
  const s = parts.length > 1 ? parts[0][0] + parts[1][0] : name.slice(0, 2)
  return s.toUpperCase()
}

const formatTime = (s) => {
  if (!s) return '0 min'
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h} h ${String(m).padStart(2, '0')}` : `${m} min`
}

export default function ClassementPageTV({ currentUser }) {
  const navigate = useNavigate()
  const [users, setUsers] = useState([])
  const [filter, setFilter] = useState('time')
  const [status, setStatus] = useState('loading') // loading | ready | error

  // Touche retour de la télécommande
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Backspace' || e.key === 'BrowserBack' || e.key === 'GoBack' || e.keyCode === 10009) {
        e.preventDefault()
        navigate('/')
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [navigate])

  // Une seule lecture : changer de critère ne fait que retrier.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const snapshot = await getDocs(collection(db, 'users'))
        const list = []
        snapshot.forEach((doc) => {
          const d = doc.data()
          const displayName = nameOf(d)
          if (!displayName) return
          list.push({
            id: doc.id,
            displayName,
            totalListenTime: watchTimeOf(d),
            streak: Number(d.currentStreak || d.streak || d.stats?.currentStreak || 0),
            sessionsCount: Number(d.sessionsCount || d.sessions || d.stats?.sessionsCount || 0)
          })
        })
        if (!cancelled) { setUsers(list); setStatus('ready') }
      } catch (err) {
        console.error('Classement TV :', err)
        if (!cancelled) setStatus('error')
      }
    })()
    return () => { cancelled = true }
  }, [])

  const current = FILTERS.find(f => f.id === filter)

  const sorted = useMemo(() => {
    const k = current.key
    return [...users].sort((a, b) =>
      b[k] !== a[k] ? b[k] - a[k] : a.displayName.localeCompare(b.displayName)
    )
  }, [users, current])

  const top = sorted.slice(0, 10)
  const podium = top.slice(0, 3)
  const others = top.slice(3)
  const max = top[0]?.[current.key] || 1

  const myIndex = currentUser ? sorted.findIndex(u => u.id === currentUser.uid) : -1
  const meOutside = myIndex >= 10 ? sorted[myIndex] : null

  const valueOf = (u) => {
    const v = u[current.key] || 0
    if (filter === 'time') return formatTime(v)
    if (filter === 'streak') return `${v} jour${v > 1 ? 's' : ''}`
    return `${v} session${v > 1 ? 's' : ''}`
  }

  const isMe = (u) => currentUser && u.id === currentUser.uid

  const Step = ({ user, rank }) => (
    <div className={`tvc-step tvc-step--${rank} ${isMe(user) ? 'is-me' : ''}`}>
      <div className="tvc-step__crown">{rank === 1 && <Crown fill="currentColor" strokeWidth={1.5} />}</div>
      <div className="tvc-avatar">{initialsOf(user.displayName)}</div>
      <div className="tvc-step__name">{user.displayName}</div>
      <div className="tvc-step__value">{valueOf(user)}</div>
      <div className="tvc-step__block">{rank}</div>
    </div>
  )

  const Row = ({ user, rank, extraClass = '' }) => (
    <div className={`tvc-row ${isMe(user) ? 'is-me' : ''} ${extraClass}`}>
      <span className="tvc-row__rank">{rank}</span>
      <span className="tvc-row__avatar">{initialsOf(user.displayName)}</span>
      <div className="tvc-row__body">
        <div className="tvc-row__name">
          {user.displayName}
          {isMe(user) && <span className="tvc-me-tag">Vous</span>}
        </div>
        <div className="tvc-row__bar">
          <i style={{ transform: `scaleX(${Math.min(1, (user[current.key] || 0) / max)})` }} />
        </div>
      </div>
      <span className="tvc-row__value">{valueOf(user)}</span>
    </div>
  )

  return (
    <div className="tvh tvc">
      <header className="tvc-head">
        <button type="button" className="tvh-btn" onClick={() => navigate('/')}>
          <ArrowLeft size="1.1em" /> Accueil
        </button>

        <div className="tvc-title">
          <span className="tvc-title__icon"><Trophy /></span>
          <div>
            <h1>Classement</h1>
            <p>Top 10 des auditeurs · {current.label.toLowerCase()}</p>
          </div>
        </div>

        <div className="tvc-filters" role="group" aria-label="Critère du classement">
          {FILTERS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`tvc-filter ${filter === id ? 'is-on' : ''}`}
              aria-pressed={filter === id}
              onClick={() => setFilter(id)}
              data-tv-initial-focus={id === 'time' ? '' : undefined}
            >
              <Icon /> <span>{label}</span>
            </button>
          ))}
        </div>
      </header>

      {status === 'loading' && (
        <div className="tvc-state">
          <div className="tvc-spinner" />
          Chargement du classement…
        </div>
      )}

      {status === 'error' && (
        <div className="tvc-state">
          <Trophy />
          <strong>Classement indisponible</strong>
          Vérifiez la connexion internet puis revenez sur cette page.
        </div>
      )}

      {status === 'ready' && top.length === 0 && (
        <div className="tvc-state">
          <Users />
          <strong>Pas encore de classement</strong>
          Écoutez la radio pour apparaître ici !
        </div>
      )}

      {status === 'ready' && top.length > 0 && (
        <div className="tvc-main">
          <section className="tvc-podium" aria-label="Podium">
            {podium[1] ? <Step user={podium[1]} rank={2} /> : <div />}
            {podium[0] && <Step user={podium[0]} rank={1} />}
            {podium[2] ? <Step user={podium[2]} rank={3} /> : <div />}
          </section>

          <section className="tvc-list" aria-label="Places 4 à 10">
            {others.map((u, i) => <Row key={u.id} user={u} rank={i + 4} />)}
            {others.length === 0 && (
              <div className="tvc-row tvc-row--empty">Les places 4 à 10 sont encore libres</div>
            )}
            {meOutside && (
              <Row user={meOutside} rank={myIndex + 1} extraClass="tvc-row--me-out" />
            )}
          </section>
        </div>
      )}

      <footer className="tvc-foot">
        <span>◀ ▶ Changer de critère · OK Choisir</span>
        <span>↩ Retour : revenir à l'accueil</span>
      </footer>
    </div>
  )
}
